import {useCallback, useState} from 'react';

import {useRouter} from 'next/router';
import {useTranslation} from 'react-i18next';

import {useAuth} from 'UI/hooks/useAuth';
import {useLocationSetting} from 'UI/hooks/useLocationSetting';

/**
 * Hook für die Einstellungsseite. Kapselt Auth-, Standort- und
 * Analyse-Opt-out-State sowie die zugehörigen Handler.
 *
 * @returns State und Handler für die Einstellungsseite.
 */
export const useSettingsPage = () => {
    const {t} = useTranslation();
    const {isAdmin, isLoading, logout, setAnalyticsOptOut, user} = useAuth();
    const {isEnabled: locationEnabled, permissionState, setEnabled: setLocationEnabled} = useLocationSetting();
    const router = useRouter();
    const [loggingOut, setLoggingOut] = useState(false);
    const [analyticsUpdating, setAnalyticsUpdating] = useState(false);

    /**
     * Meldet den User ab und navigiert zur Startseite.
     */
    const handleLogout = useCallback(async () => {
        setLoggingOut(true);

        try {
            await logout();
            await router.push('/');
        } finally {
            setLoggingOut(false);
        }
    }, [logout, router]);

    /**
     * Schaltet den Standort-Zugriff um.
     */
    const toggleLocation = useCallback(() => {
        setLocationEnabled(!locationEnabled);
    }, [locationEnabled, setLocationEnabled]);

    /**
     * Schaltet die Verknüpfung der Analyse-Sessions mit dem Konto um (Widerspruch nach Art. 21 DSGVO).
     * Beim Deaktivieren wird zusätzlich ausgeloggt: `identify()` wird nur beim Login neu ausgewertet,
     * ohne Logout würde die bereits verknüpfte Umami-Session in der aktuellen Sitzung weiterlaufen.
     *
     * @param linkingEnabled Der neue gewünschte Zustand (true = verknüpfen, false = widersprechen).
     */
    const handleAnalyticsLinkingToggle = useCallback(async (linkingEnabled: boolean) => {
        setAnalyticsUpdating(true);

        try {
            await setAnalyticsOptOut(!linkingEnabled);

            if (!linkingEnabled) {
                await logout();
                await router.push('/');
            }
        } finally {
            setAnalyticsUpdating(false);
        }
    }, [logout, router, setAnalyticsOptOut]);

    return {
        analyticsUpdating,
        handleAnalyticsLinkingToggle,
        handleLogout,
        isAdmin,
        isLoading,
        locationEnabled,
        loggingOut,
        permissionState,
        t,
        toggleLocation,
        user
    };
};